/**
 * HardDeleteTrigger
 *
 * The shared destructive toolbar control for the Praetorian-only permanent
 * ("hard") delete. Standardizes three things across every bulk toolbar:
 *
 * 1. A `variant="destructive"` button.
 * 2. Availability: the control stays VISIBLE but becomes DISABLED when the
 *    selection can't be hard-deleted — never hidden, never error-after-click.
 * 3. Accessibility: when disabled by a gating reason, the button is wrapped in
 *    a focusable `<span>` inside a Tooltip so the reason is reachable by
 *    keyboard and exposed to assistive tech (a bare `title` attribute is NOT
 *    announced on a disabled button). Mirrors the RetestButton precedent
 *    (`sections/vulnerabilities/components/actions/RetestButton.tsx`).
 *
 * The gating reason string is owned by `getHardDeleteDisabledReason` in
 * `constants/hardDelete` so copy can't drift across entities.
 *
 * @module components/actions
 */

import { TrashIcon } from '@heroicons/react/24/outline';

import { Tooltip } from '@/components/Tooltip';
import { Button, type ButtonProps } from '@/components/ui/button';

export interface HardDeleteTriggerProps {
  /**
   * Reason the control is gated, from `getHardDeleteDisabledReason`. When
   * non-null the button is disabled and the reason is shown in a
   * keyboard/AT-reachable tooltip. `null` means the selection is eligible.
   */
  disabledReason: string | null;
  /** Fired when the (enabled) button is clicked. */
  onClick: () => void;
  /** Button label (default: "Delete Permanently"). */
  label?: string;
  /**
   * Extra disable independent of the gating reason (e.g. empty selection or
   * a mutation in flight). No tooltip is shown for this — it isn't a policy
   * block, just a "nothing to act on" state.
   */
  disabled?: boolean;
  /** Button size (default: "sm" to match toolbar siblings). */
  size?: ButtonProps['size'];
  /** Test id forwarded to the button; the locked wrapper gets `${id}-locked`. */
  'data-testid'?: string;
}

export function HardDeleteTrigger({
  disabledReason,
  onClick,
  label = 'Delete Permanently',
  disabled = false,
  size = 'sm',
  'data-testid': testId = 'hard-delete-button',
}: HardDeleteTriggerProps) {
  const blocked = disabledReason !== null;

  const button = (
    <Button
      variant="destructive"
      size={size}
      onClick={onClick}
      disabled={disabled || blocked}
      data-testid={testId}
      aria-label={label}
    >
      <TrashIcon className="size-4" />
      {label}
    </Button>
  );

  // Not policy-blocked: render the button as-is (it may still be plainly
  // disabled via `disabled`, which needs no explanatory tooltip).
  if (!blocked) return button;

  // Policy-blocked: keep the affordance visible but disabled, with a
  // keyboard-reachable, AT-exposed reason. The focusable <span> lets the
  // tooltip fire on a disabled button (mirrors RetestButton.tsx:110-118).
  return (
    <Tooltip title={disabledReason}>
      <span tabIndex={0} data-testid={`${testId}-locked`}>
        {button}
      </span>
    </Tooltip>
  );
}
